import React, { useState, useEffect, useRef, useCallback } from 'react'
import { AdminLayout } from '../components/AdminLayout'
import { adminApi } from '../services/adminApi'
import {
  Target,
  Zap,
  Clock,
  AlertTriangle,
  Play,
  Square,
  Activity,
  BarChart3,
  FileText
} from 'lucide-react'

export const TestingPage = () => {
  const [duration, setDuration] = useState(1)
  const [rate, setRate] = useState(10)
  const [functionName, setFunctionName] = useState('')
  const [isRunning, setIsRunning] = useState(false)
  const [sessionId, setSessionId] = useState(null)
  const [stats, setStats] = useState(null)
  const [error, setError] = useState('')
  const [logs, setLogs] = useState([])

  const pollIntervalRef = useRef(null)

  // Available functions for testing
  const availableFunctions = [
    'COMPOSITE_0',
    'BatchNorm2d_relu_COMPOSITE_53',
    'MaxPool2d_COMPOSITE_99',
    'Sequential_Bottleneck_BatchNorm2d_relu_COMPOSITE_51',
    'Sequential_Bottleneck_BatchNorm2d_add_COMPOSITE_101',
    'AdaptiveAvgPool2d_0_COMPOSITE_54',
    'Linear_COMPOSITE_133'
  ]

  const handleStartStressTest = async () => {
    try {
      setError('')
      setLogs([])

      const response = await adminApi.startAK47({
        duration_minutes: duration,
        rate_per_second: rate,
        function_name: functionName || null
      })

      setSessionId(response.session_id)
      setIsRunning(true)

      addLog(`🎯 AK47 mag dump initiated: ${response.total_payloads} payloads over ${duration} minutes`)
      addLog(`⚡ Rate: ${rate} payloads/second`)
      addLog(`🎯 Function: ${functionName || 'RANDOM'}`)

      // Start polling for updates
      startPolling(response.session_id)
    } catch (err) {
      setError(`Failed to start mag dump: ${err.message}`)
    }
  }

  const handleStopStressTest = async () => {
    if (!sessionId) return

    try {
      await adminApi.stopAK47(sessionId)
      addLog('🛑 Cease fire command sent')
      setIsRunning(false)
      stopPolling()
    } catch (err) {
      setError(`Failed to stop mag dump: ${err.message}`)
    }
  }

  const startPolling = useCallback((sessionId) => {
    pollIntervalRef.current = setInterval(async () => {
      try {
        // Get session status
        const statusResponse = await adminApi.getAK47Status(sessionId)

        if (!statusResponse.active) {
          setIsRunning(false)
          stopPolling()
          addLog(`✅ Mag dump completed: ${statusResponse.payloads_fired} payloads fired`)
        }

        // Get overall stats
        const statsResponse = await adminApi.getAK47Stats()
        setStats({
          ...statusResponse,
          ...statsResponse
        })
      } catch (err) {
        console.error('Polling error:', err)
      }
    }, 2000)
  }, [])

  const stopPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current)
      pollIntervalRef.current = null
    }
  }

  const addLog = (message) => {
    const timestamp = new Date().toLocaleTimeString()
    setLogs((prev) => [...prev, `[${timestamp}] ${message}`])
  }

  useEffect(() => {
    return () => {
      stopPolling()
      if (isRunning && sessionId) {
        adminApi.stopAK47(sessionId).catch(console.error)
      }
    }
  }, [isRunning, sessionId])

  const totalPayloads = duration * 60 * rate

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">AK47 Stress Testing</h1>
            <p className="text-gray-500 mt-1">
              Load test ML functions with rapid payload delivery
            </p>
          </div>
          {isRunning && (
            <button
              onClick={handleStopStressTest}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <Square className="h-4 w-4" />
              Cease Fire
            </button>
          )}
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-800">Error</p>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">
              Test Configuration
            </h2>

            {!isRunning ? (
              <div className="space-y-6">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-center space-x-2">
                    <AlertTriangle className="h-5 w-5 text-yellow-600" />
                    <span className="text-yellow-800 font-medium">Warning</span>
                  </div>
                  <p className="text-yellow-700 mt-2 text-sm">
                    This will rapidly fire payloads to the AK47 Redis queue for
                    performance testing. Monitor system resources during the test.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Clock className="h-4 w-4 inline mr-1" />
                      Duration (minutes)
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={duration}
                      onChange={(e) => setDuration(parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Maximum 10 minutes</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Zap className="h-4 w-4 inline mr-1" />
                      Rate (payloads/second)
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="50"
                      value={rate}
                      onChange={(e) => setRate(parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Maximum 50/second</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Target className="h-4 w-4 inline mr-1" />
                    Target Function (optional)
                  </label>
                  <select
                    value={functionName}
                    onChange={(e) => setFunctionName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Random Functions</option>
                    {availableFunctions.map((func) => (
                      <option key={func} value={func}>
                        {func}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Leave blank to test random functions
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-2">
                    Test Preview
                  </h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Total Payloads:</span>
                      <span className="font-bold ml-2">
                        {totalPayloads.toLocaleString()}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Est. Duration:</span>
                      <span className="font-bold ml-2">
                        {duration} minute{duration !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Rate:</span>
                      <span className="font-bold ml-2">{rate}/sec</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Function:</span>
                      <span className="font-bold ml-2">
                        {functionName || 'Random'}
                      </span>
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleStartStressTest}
                  className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-4 rounded-lg flex items-center justify-center space-x-2 transition-colors"
                >
                  <Play className="h-5 w-5" />
                  <span>FIRE AK47</span>
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center space-x-2">
                    <Activity className="h-5 w-5 text-red-600 animate-pulse" />
                    <span className="font-bold text-red-800">AK47 FIRING</span>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-2">
                    Active Configuration
                  </h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Duration:</span>
                      <span className="font-bold ml-2">{duration}m</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Rate:</span>
                      <span className="font-bold ml-2">{rate}/sec</span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-gray-600">Function:</span>
                      <span className="font-bold ml-2">
                        {functionName || 'Random'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-6">
            {stats && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <BarChart3 className="h-5 w-5 text-gray-600" />
                  <h2 className="text-lg font-semibold text-gray-900">
                    Live Statistics
                  </h2>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Payloads Fired:</span>
                    <span className="font-bold ml-2 text-red-600">
                      {stats.payloads_fired?.toLocaleString()}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Queue Depth:</span>
                    <span className="font-bold ml-2">
                      {stats.queue_depth?.toLocaleString()}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Rate:</span>
                    <span className="font-bold ml-2">
                      {stats.rate_per_second}/sec
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Total Jobs:</span>
                    <span className="font-bold ml-2">
                      {stats.total_jobs_submitted?.toLocaleString()}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Active Sessions:</span>
                    <span className="font-bold ml-2">
                      {stats.active_sessions || 0}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center space-x-2 mb-4">
                <FileText className="h-5 w-5 text-gray-600" />
                <h2 className="text-lg font-semibold text-gray-900">
                  Console Output
                </h2>
              </div>
              <div className="bg-black text-green-400 font-mono text-xs p-4 rounded-lg h-96 overflow-y-auto">
                {logs.length === 0 ? (
                  <div className="text-gray-500">Waiting for output...</div>
                ) : (
                  logs.map((log, index) => (
                    <div key={index} className="mb-1">
                      {log}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  )
}
